import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getEnvelopes } from '@/services/api';
import type { Envelope } from '@/types';
import EnvelopeCard from '@/components/EnvelopeCard';
import FloatingParticles from '@/components/FloatingParticles';
import LoveAura from '@/components/LoveAura';
import GlowingText from '@/components/GlowingText';
import RippleEffect from '@/components/RippleEffect';

const Home = () => {
    const [envelopes, setEnvelopes] = useState<Envelope[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        const fetchEnvelopes = async () => {
            try {
                setLoading(true);
                const response = await getEnvelopes(page);
                setEnvelopes(response.items);
                setTotalPages(response.total_pages);
            } catch (error) {
                console.error('Failed to fetch envelopes:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchEnvelopes();
    }, [page]);

    return (
        <div className="min-h-screen bg-romance-cream relative overflow-hidden">
            {/* JavaScript-powered canvas animations */}
            <FloatingParticles />
            <LoveAura />
            <RippleEffect />
            
            {/* Elegant floating particles - sparkles, light orbs, and rose petals */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden">
                {/* Golden sparkles */}
                {[...Array(8)].map((_, i) => (
                    <div
                        key={`sparkle-${i}`}
                        className="sparkle-float"
                        style={{
                            left: `${10 + i * 12}%`,
                            animationDelay: `${i * 1.5}s`,
                            animationDuration: `${12 + i * 2}s`
                        }}
                    />
                ))}
                
                {/* Soft light orbs */}
                {[...Array(6)].map((_, i) => (
                    <div
                        key={`orb-${i}`}
                        className="light-orb"
                        style={{
                            left: `${15 + i * 15}%`,
                            animationDelay: `${i * 2}s`,
                            animationDuration: `${18 + i * 3}s`
                        }}
                    />
                ))}
                
                {/* Gentle rose petals */}
                {[...Array(5)].map((_, i) => (
                    <div
                        key={`petal-${i}`}
                        className="rose-petal"
                        style={{
                            left: `${20 + i * 15}%`,
                            animationDelay: `${i * 2.5}s`,
                            animationDuration: `${20 + i * 4}s`
                        }}
                    />
                ))}
                
                {/* Floating butterflies
                {[...Array(4)].map((_, i) => (
                    <div
                        key={`butterfly-${i}`}
                        className="butterfly-float"
                        style={{
                            left: `${15 + i * 25}%`,
                            animationDelay: `${i * 3}s`,
                            animationDuration: `${15 + i * 3}s`,
                            fontSize: '24px',
                        }}
                    >
                        🦋
                    </div>
                ))} */}
                
                {/* Floating love letters
                {[...Array(3)].map((_, i) => (
                    <div
                        key={`letter-${i}`}
                        className="letter-drift"
                        style={{
                            left: `${10 + i * 35}%`,
                            animationDelay: `${i * 4}s`,
                            fontSize: '20px',
                        }}
                    >
                        💌
                    </div>
                ))} */}
                
                {/* Twinkling stars
                {[...Array(10)].map((_, i) => (
                    <div
                        key={`star-${i}`}
                        className="star-twinkle"
                        style={{
                            left: `${5 + i * 10}%`,
                            top: `${10 + (i % 3) * 20}%`,
                            animationDelay: `${i * 0.8}s`,
                        }}
                    >
                        ✨
                    </div>
                ))} */}
                
                {/* Bougainvillea petals
                {[...Array(6)].map((_, i) => (
                    <div
                        key={`bougainvillea-${i}`}
                        className="bougainvillea-fall"
                        style={{
                            left: `${12 + i * 16}%`,
                            animationDelay: `${i * 2.5}s`,
                            fontSize: '18px',
                        }}
                    >
                        🌺
                    </div>
                ))} */}
                
                {/* Floating hearts at the top
                {[...Array(8)].map((_, i) => (
                    <div
                        key={`heart-${i}`}
                        className="heart-float"
                        style={{
                            left: `${8 + i * 12}%`,
                            animationDelay: `${i * 1.5}s`,
                            animationDuration: `${12 + i * 2}s`,
                            fontSize: i % 2 === 0 ? '20px' : '16px',
                        }}
                    >
                        {i % 3 === 0 ? '💕' : i % 3 === 1 ? '💖' : '💗'}
                    </div>
                ))} */}
                
                {/* Drifting feathers
                {[...Array(3)].map((_, i) => (
                    <div
                        key={`feather-${i}`}
                        className="feather-drift"
                        style={{
                            left: `${20 + i * 30}%`,
                            animationDelay: `${i * 5}s`,
                            fontSize: '22px',
                        }}
                    >
                        🪶
                    </div>
                ))} */}
            </div>

            {/* Paper texture overlay */}
            <div className="fixed inset-0 paper-texture-romantic pointer-events-none opacity-30"></div>

            {/* Vignette overlay */}
            <div className="fixed inset-0 vignette-glow pointer-events-none"></div>

            {/* Header - Opening a sealed letter */}
            <header className="relative py-12 sm:py-16 md:py-20 lg:py-24 overflow-hidden">
                {/* Watercolor wash background */}
                <div className="absolute inset-0 bg-gradient-to-b from-romance-blush/20 via-transparent to-transparent"></div>
                
                {/* Abstract Picasso-inspired curves */}
                <svg className="absolute top-0 right-0 w-48 h-48 sm:w-64 sm:h-64 opacity-5 text-romance-wine" viewBox="0 0 200 200">
                    <path d="M 50,100 Q 100,50 150,100 T 250,100" stroke="currentColor" strokeWidth="2" fill="none" />
                    <circle cx="100" cy="100" r="40" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.3" />
                </svg>

                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    {/* Main Title - Byronic flourish with interactive glow */}
                    <GlowingText text="~sincerelyyourcaffeine" className="mb-4 sm:mb-6 romantic-title" />
                    
                    {/* Tender subtitle */}
                    <p className="text-center text-romance-brown font-cormorant text-lg sm:text-xl md:text-2xl italic max-w-3xl mx-auto leading-relaxed mb-3 sm:mb-4 animate-fade-in-slow px-4">
                        A sanctuary of the words I never said aloud,<br className="hidden sm:inline" />
                        <span className="sm:hidden"> </span>yet felt endlessly.
                    </p>

                    {/* Dedication line */}
                    <p className="text-center text-romance-wine font-dancing text-xl sm:text-2xl md:text-3xl mt-6 sm:mt-8 animate-fade-in-slower px-4">
                        For my love, my radiant lamp ♥︎
                    </p>

                    {/* Decorative flourish */}
                    <div className="flex justify-center mt-8">
                        <div className="w-24 h-px bg-gradient-to-r from-transparent via-romance-gold to-transparent"></div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 py-12 relative z-10">
                {loading ? (
                    <div className="flex flex-col justify-center items-center min-h-[400px]">
                        <div className="text-romance-brown text-2xl font-cormorant italic mb-4 animate-pulse">
                            Unsealing letters...
                        </div>
                        <div className="w-16 h-1 bg-gradient-to-r from-transparent via-romance-gold to-transparent"></div>
                    </div>
                ) : envelopes.length === 0 ? (
                    <div className="text-center py-20">
                        <p className="text-3xl text-romance-ink font-playfair mb-4">The letters rest in silence...</p>
                        <p className="text-romance-brown font-cormorant text-lg italic">New stories will arrive like whispered confessions.</p>
                    </div>
                ) : (
                    <>
                        {/* Section title */}
                        <div className="text-center mb-8 sm:mb-12 px-4">
                            <h2 className="text-2xl sm:text-3xl md:text-4xl font-playfair font-bold text-romance-ink mb-3 leading-tight">
                                The Letters I Wrote Between Heartbeats
                            </h2>
                            <p className="text-romance-brown font-cormorant italic text-base sm:text-lg">
                                Each envelope holds a piece of my soul
                            </p>
                            <div className="flex justify-center mt-4">
                                <div className="w-24 sm:w-32 h-px bg-gradient-to-r from-transparent via-romance-blush to-transparent"></div>
                            </div>
                        </div>

                        {/* Envelope Grid - Stories on a shelf */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                            {envelopes.map((envelope, index) => (
                                <Link 
                                    key={envelope.id} 
                                    to={`/envelope/${envelope.slug}`}
                                    className="romantic-envelope-card"
                                    style={{ animationDelay: `${index * 0.1}s` }}
                                >
                                    <EnvelopeCard envelope={envelope} />
                                </Link>
                            ))}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex justify-center items-center space-x-6 mb-12">
                                <button
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="romantic-btn disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                    ← Previous
                                </button>
                                <span className="text-romance-ink font-cormorant text-lg">
                                    Chapter {page} of {totalPages}
                                </span>
                                <button
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages}
                                    className="romantic-btn disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                    Next →
                                </button>
                            </div>
                        )}
                    </>
                )}
            </main>

            {/* Footer - A quiet whisper */}
            <footer className="relative py-12 mt-20 overflow-hidden">
                {/* Soft gradient background */}
                <div className="absolute inset-0 bg-gradient-to-t from-romance-blush/10 via-transparent to-transparent"></div>
                
                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center">
                        {/* Main footer message */}
                        <p className="font-cormorant text-xl text-romance-brown mb-3 flex items-center justify-center gap-2">
                            Made with 
                            <span className="text-romance-wine inline-block animate-heartbeat">❤️</span>
                            by{' '}
                            <span className="font-dancing text-2xl text-romance-ink">your caffeine</span>
                        </p>
                        
                        {/* Signature line */}
                        <p className="font-dancing text-2xl text-romance-wine mb-4">
                            — and for my love, my radiant lamp ♥︎
                        </p>

                        {/* Decorative flourish */}
                        <div className="flex justify-center mb-4">
                            <svg className="w-24 h-8 text-romance-gold opacity-40" viewBox="0 0 100 30">
                                <path d="M 0,15 Q 25,5 50,15 T 100,15" stroke="currentColor" strokeWidth="0.5" fill="none" />
                                <circle cx="50" cy="15" r="2" fill="currentColor" />
                            </svg>
                        </div>

                        {/* Subtle admin link */}
                        <Link 
                            to="/admin" 
                            className="text-romance-brown/40 hover:text-romance-brown text-xs font-cormorant inline-block transition-all duration-500 hover:tracking-wider"
                        >
                            ⚙️
                        </Link>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Home;



